import { useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { Input } from "@akuna/ui";
import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";
import type { PrintLayoutFormValues } from "../printLayoutForm";
import type { ImageUploader } from "../useImageUpload";
import { ImageField } from "./ImageField";
import { PanelShell } from "./PanelShell";

const PAPERS = [
  { value: "A4", key: "a4" },
  { value: "A5", key: "a5" },
  { value: "Letter", key: "letter" },
] as const;

const MARGINS = [
  { name: "marginTopMm", key: "marginTop" },
  { name: "marginBottomMm", key: "marginBottom" },
  { name: "marginSideMm", key: "marginSide" },
] as const;

export function PaperPanel({ images }: { images: ImageUploader }) {
  const { t } = useTranslation("settings");
  const { register, watch, setValue, formState } = useFormContext<PrintLayoutFormValues>();
  const paper = watch("paper");

  return (
    <PanelShell title={t("printEditor.areas.page")}>
      <div className="flex flex-col gap-2">
        <Label id="print-paper-label">{t("printEditor.paper.size")}</Label>
        <div role="group" aria-labelledby="print-paper-label" className="grid grid-cols-3 gap-2">
          {PAPERS.map(({ value, key }) => (
            <button
              key={value}
              type="button"
              aria-pressed={paper === value}
              onClick={() => setValue("paper", value, { shouldDirty: true })}
              className={cn(
                "rounded-xl border py-2.5 text-[13px] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                paper === value && "border-2 border-primary bg-accent font-semibold",
              )}
            >
              {t(`printEditor.paper.${key}`)}
            </button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-3 gap-2">
        {MARGINS.map(({ name, key }) => {
          const error = formState.errors[name]?.message;
          return (
            <div key={name} className="flex flex-col gap-1.5">
              <Label htmlFor={`print-${name}`} className="text-xs">
                {t(`printEditor.paper.${key}`)}
              </Label>
              <div className="relative">
                <Input
                  id={`print-${name}`}
                  type="number"
                  inputMode="numeric"
                  min={name === "marginBottomMm" ? 8 : 5}
                  max={40}
                  step={1}
                  invalid={!!error}
                  aria-describedby={error ? `print-${name}-error` : undefined}
                  className="pr-9"
                  {...register(name, { valueAsNumber: true })}
                />
                <span aria-hidden="true" className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-xs text-muted-foreground">
                  {t("printEditor.paper.mm")}
                </span>
              </div>
              {error ? (
                <p id={`print-${name}-error`} role="alert" className="text-xs text-destructive">
                  {String(error)}
                </p>
              ) : null}
            </div>
          );
        })}
      </div>

      <ImageField
        kind="background"
        label={t("printEditor.paper.backgroundImage")}
        url={watch("backgroundImageUrl")}
        images={images}
        showDropzoneHint
        onClear={() => {
          setValue("backgroundImageUrl", "", { shouldDirty: true });
          images.clearPending("background");
        }}
      />
    </PanelShell>
  );
}
